<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBRL } from 'dashboard/helper/lais/currency';

const props = defineProps({
  loading: { type: Boolean, default: false },
  summary: { type: Array, default: () => [] },
});

const emit = defineEmits(['view-production']);
const { t } = useI18n();
const expanded = ref(false);
const productionStatusKeys = [
  'em_andamento',
  'liberado',
  'solicitado',
  'em_transferencia',
  'concluido',
  'cancelado',
];
const total = computed(
  () =>
    props.summary.find(item => item.key === 'total') || {
      key: 'total',
      count: 0,
      valor: { amount: 0 },
    }
);
const details = computed(() =>
  productionStatusKeys.map(
    key =>
      props.summary.find(item => item.key === key) || {
        key,
        count: 0,
        valor: { amount: 0 },
      }
  )
);
const productionCount = computed(() =>
  details.value.reduce((count, item) => count + Number(item.count || 0), 0)
);
const hasProductionSummary = computed(() =>
  props.summary.some(item => item.key === 'total')
);
const money = value => formatBRL(value?.amount ?? 0, { fractionDigits: 2 });
const currencySymbol = formatBRL(0).split(' ')[0];
const statusLabel = key =>
  ({
    em_andamento: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.EM_ANDAMENTO'),
    liberado: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.LIBERADO'),
    solicitado: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.SOLICITADO'),
    em_transferencia: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.EM_TRANSFERENCIA'),
    concluido: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.CONCLUIDO'),
    cancelado: t('REAL_ESTATE_BROKER_DASHBOARD.STATUS.CANCELADO'),
  })[key] || t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.NOT_INFORMED');
const chipClass = key =>
  ({
    em_andamento: 'bg-[#FFF7E5] text-[#D69500]',
    liberado: 'bg-[#E7F4D7] text-[#6B9539]',
    solicitado: 'bg-[#EDF8F7] text-[#01988C]',
    em_transferencia: 'bg-[#E7F4D7] text-[#6B9539]',
    concluido: 'bg-[#E7F4D7] text-[#6B9539]',
    cancelado: 'bg-[#FCE9E9] text-[#E33838]',
  })[key] || 'bg-slate-100 text-slate-700';
</script>

<template>
  <section
    data-testid="broker-production-accordion"
    class="mx-2 min-h-[162px] overflow-hidden rounded-[10px] border border-[#E0E7EB] bg-[#EDF8F7] shadow-[0_0_6px_0_rgba(0,0,0,0.02),0_2px_4px_0_rgba(0,0,0,0.08)] md:hidden"
    :aria-busy="loading"
  >
    <button
      type="button"
      class="relative flex w-full flex-col items-center px-[21px] pt-[21px] text-center"
      data-testid="broker-production-toggle"
      :aria-expanded="expanded"
      aria-controls="broker-production-details"
      @click="expanded = !expanded"
    >
      <span
        class="text-[12px] font-bold uppercase leading-[18px] text-[#01988C]"
        data-testid="broker-production-label"
      >
        {{ t('REAL_ESTATE_BROKER_DASHBOARD.PRODUCTION.TOTAL') }}
      </span>
      <span
        v-if="loading"
        class="mt-4 block h-12 w-40 animate-pulse rounded bg-white/70"
        data-testid="broker-production-total-loading"
      />
      <span
        v-else
        class="mt-4 block min-h-12 text-[40px] font-bold leading-[48px] text-[#01988C]"
        data-testid="broker-production-total"
      >
        {{ money(total.valor) }}
      </span>
      <span
        class="absolute right-[25px] top-3 size-5 text-lais-text-muted transition-transform"
        :class="
          expanded ? 'rotate-180 i-lucide-chevron-up' : 'i-lucide-chevron-down'
        "
        aria-hidden="true"
        data-testid="broker-production-chevron"
      />
    </button>
    <ul
      v-if="expanded"
      id="broker-production-details"
      class="w-full px-[21px]"
      data-testid="broker-production-details"
    >
      <li
        v-for="item in details"
        :key="item.key"
        :data-testid="`broker-production-status-${item.key}`"
        class="my-2 flex w-full items-center justify-between gap-2 bg-white p-5"
      >
        <span
          class="rounded-full px-2 py-1 text-xs font-medium"
          :class="chipClass(item.key)"
        >
          {{ statusLabel(item.key) }}
        </span>
        <span
          v-if="loading"
          class="flex items-center gap-1 text-sm text-[#464F53]"
        >
          {{ currencySymbol }}
          <span class="h-4 w-24 animate-pulse rounded bg-[#E0E7EB]" />
        </span>
        <span v-else class="text-right text-sm font-normal text-[#464F53]">
          {{ money(item.valor) }}
        </span>
      </li>
    </ul>
    <button
      v-if="hasProductionSummary"
      type="button"
      class="mx-auto mt-4 flex px-[21px] pb-[21px] text-sm font-semibold text-[#01988C] hover:underline"
      data-testid="broker-production-view"
      @click.stop="emit('view-production', productionCount)"
    >
      {{
        t('REAL_ESTATE_BROKER_DASHBOARD.PRODUCTION.VIEW', {
          count: productionCount,
        })
      }}
    </button>
  </section>
</template>
